import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { ChatsTutorialService } from "../../services/Chats/ChatsTutorialService";
import { ChatMessageModel } from "../../models/Chat/ChatMessageModel";

interface ContextProps {
  tutorial: any;
  stepTutorial: number;
  isLoadingTutorial: boolean;
  isFinished: boolean;
  error: string;
  setError: Dispatch<SetStateAction<any>>;
  messageTutorial: string;
  messages: any;
  agentFinalText: string;
  isStreaming: boolean;
  isVoting: boolean;
  setMessageTutorial: Dispatch<SetStateAction<string>>;
  setMessages: Dispatch<SetStateAction<any>>;
  setStepTutorial: Dispatch<SetStateAction<number>>;
  handleList: () => void;
  handleSendTutorial: (message: string) => void;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  resetTutorial: () => void;
}

export const ChatsTutorialContext = createContext({} as ContextProps);

interface ProviderProps {
  children: ReactNode;
}

const STORAGE_STEP_KEY = "@tutorial:step";
const STORAGE_MESSAGES_KEY = "@tutorial:messages";

export const ChatsTutorialProvider = ({ children }: ProviderProps) => {
  const [tutorial, setTutorial] = useState<any>(null);
  const [messages, setMessages] = useState<ChatMessageModel[]>([]);
  const [stepTutorial, setStepTutorial] = useState(0);
  const [messageTutorial, setMessageTutorial] = useState("");
  const [isLoadingTutorial, setIsLoadingTutorial] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [agentFinalText] = useState<string>("");
  const [error, setError] = useState("");

  const service = new ChatsTutorialService();
  const location = useLocation();

  const resetTutorial = () => {
    localStorage.removeItem(STORAGE_STEP_KEY);
    localStorage.removeItem(STORAGE_MESSAGES_KEY);

    setMessages([]);
    setStepTutorial(0);
    setIsFinished(false);
    setMessageTutorial("");
  };

  const normalizeMessagesForStorage = (messages: ChatMessageModel[]) => {
    return messages.map((m) => {
      if (m.sender === "agent") {
        return {
          ...m,
          content: m.content || m.fullContent || "",
          fullContent: "",
          isStreaming: false,
          isReplay: true,
        };
      }

      return {
        ...m,
        isStreaming: false,
      };
    });
  };

  const handleList = useCallback(async () => {
    const [response, err] = await service.listSteps();
    if (err) {
      setError(response?.message || err);
      return;
    }

    setTutorial(response?.data);
  }, [location.pathname]);

  const handleSendTutorial = async (message: string) => {
    try {
      setIsLoadingTutorial(true);
      setMessageTutorial("");

      const userMessage: ChatMessageModel = {
        idChatTutorial: crypto.randomUUID(),
        content: message,
        fullContent: "",
        sender: "user",
        created_at: new Date().toISOString(),
        articles: [],
        isStreaming: true,
        is_remove: true,
      };

      setMessages((prev) => [...prev, userMessage]);

      const nextStepMap: Record<string, number> = {
        OLÁ: 1,
        VAMOS: 2,
        QUERO: 3,
        OK: 4,
        SIM: 5,
        OURO: 6,
        PERFEITO: 7,
        VAMOOOS: 8,
      };

      if (nextStepMap[message]) {
        setStepTutorial(nextStepMap[message]);
      }

      setIsLoadingTutorial(false);
      return true;
    } catch {
      setError("Erro ao enviar mensagem.");
      setIsLoadingTutorial(false);
      return false;
    }
  };

  useEffect(() => {
    handleList();
  }, []);

  useEffect(() => {
    const storedMessages = localStorage.getItem(STORAGE_MESSAGES_KEY);
    const storedStep = localStorage.getItem(STORAGE_STEP_KEY);

    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }

    if (storedStep) {
      setStepTutorial(Number(storedStep));
    }
  }, []);

  useEffect(() => {
    const normalized = normalizeMessagesForStorage(messages);
    localStorage.setItem(STORAGE_MESSAGES_KEY, JSON.stringify(normalized));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem(STORAGE_STEP_KEY, stepTutorial.toString());

    if (stepTutorial === 8) {
      setIsFinished(true);
    }
  }, [stepTutorial]);

  useEffect(() => {
    if (!tutorial || stepTutorial === 0) return;

    const stepData = tutorial.messages?.find(
      (m: any) => m.step === stepTutorial,
    );

    if (!stepData || !stepData.fullContent) return;

    const messageId = `tutorial-step-${stepTutorial}`;

    const alreadyExists = messages.find(
      (m: any) => m.idChatTutorial === messageId,
    );

    if (alreadyExists?.isReplay) return;

    setIsStreaming(true);

    const agentMessage: ChatMessageModel = {
      idChatTutorial: `tutorial-step-${stepTutorial}`,
      content: "",
      fullContent: stepData.fullContent,
      sender: "agent",
      created_at: new Date().toISOString(),
      articles: [],
      isStreaming: true,
      isReplay: false,
    };

    const timeout = setTimeout(() => {
      setMessages((prev: any) => {
        const exists = prev.some(
          (m: any) => m.idChatTutorial === agentMessage.idChatTutorial,
        );

        if (exists) return prev;

        return [...prev, agentMessage];
      });

      setIsStreaming(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [tutorial, stepTutorial]);

  return (
    <ChatsTutorialContext.Provider
      value={{
        tutorial,
        isLoadingTutorial,
        error,
        messageTutorial,
        messages,
        stepTutorial,
        agentFinalText,
        isStreaming,
        isVoting,
        isFinished,
        setError,
        setMessageTutorial,
        handleList,
        setMessages,
        setStepTutorial,
        handleSendTutorial,
        setIsStreaming,
        resetTutorial,
      }}
    >
      {children}
    </ChatsTutorialContext.Provider>
  );
};

export const useChatsTutorial = () => useContext(ChatsTutorialContext);
